import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import type { KeyboardEvent as ReactKeyboardEvent } from "react";
import { Icons } from "../../../icons";
import {
  CalendarGrid,
  DatePickerPanel,
  DatePickerRoot,
  DatePickerTrigger,
  DayButton,
  ModeButton,
  ModeSelector,
  MonthButton,
  MonthGrid,
  ScreenReaderText,
  TriggerChevron,
  Weekday,
  YearArrowIcon,
  YearButton,
  YearNavigation,
  YearOption,
  YearOptions,
} from "./DatePicker.styles";
import type {
  CalendarDate,
  DatePickerProps,
  DatePickerValue,
} from "./DatePicker.types";
import {
  buildCalendarDays,
  clampCalendarDateToBounds,
  DEFAULT_DATE_PICKER_LOCALE,
  DEFAULT_DATE_PICKER_TIME_ZONE,
  formatDatePickerValue,
  formatMonthName,
  getCalendarDateInTimeZone,
  getVisibleYears,
  isDateWithinBounds,
  isMonthWithinBounds,
  isSameCalendarDate,
  isYearWithinBounds,
  MONTHS,
  WEEKDAY_LABELS,
} from "./datePicker.utils";

const toCalendarDate = (value: DatePickerValue): CalendarDate => ({
  year: value.year,
  month: value.month,
  day: value.mode === "day" ? value.day : 1,
});

const createDraftValue = (
  value: DatePickerValue | null,
  today: CalendarDate,
  selectionMode: NonNullable<DatePickerProps["selectionMode"]>,
  minDate?: CalendarDate,
  maxDate?: CalendarDate,
): DatePickerValue => {
  const preferredDate = value ? toCalendarDate(value) : today;
  const boundedDate = clampCalendarDateToBounds(
    preferredDate,
    minDate,
    maxDate,
  );
  const mode =
    selectionMode === "both" ? (value?.mode ?? "month") : selectionMode;

  return mode === "day"
    ? { mode, ...boundedDate }
    : {
        mode,
        year: boundedDate.year,
        month: boundedDate.month,
      };
};

const isSameDatePickerValue = (
  left: DatePickerValue,
  right: DatePickerValue,
): boolean =>
  left.mode === right.mode &&
  left.year === right.year &&
  left.month === right.month &&
  (left.mode === "month" ||
    (right.mode === "day" && left.day === right.day));

const getInitialYearWindowStart = (
  currentYear: number,
  selectedYear: number,
  currentYearPosition: NonNullable<
    DatePickerProps["currentYearPosition"]
  >,
): number => {
  const initialYears = getVisibleYears(currentYear, currentYearPosition);
  const firstYear = initialYears[0];
  const lastYear = initialYears[initialYears.length - 1];

  if (selectedYear < firstYear) return selectedYear;
  if (selectedYear > lastYear) return selectedYear - initialYears.length + 1;

  return firstYear;
};

export const DatePicker = ({
  value,
  onChange,
  selectionMode = "both",
  placeholder = "Seleccioná un período",
  minDate,
  maxDate,
  locale = DEFAULT_DATE_PICKER_LOCALE,
  timeZone = DEFAULT_DATE_PICKER_TIME_ZONE,
  currentYearPosition = "center",
  appearance = "default",
  disabled = false,
  ariaLabel,
  ariaDescribedBy,
  className,
}: DatePickerProps) => {
  const panelId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const monthRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const dayRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const today = useMemo(
    () => getCalendarDateInTimeZone(new Date(), timeZone),
    [timeZone],
  );
  const initialDraft = useMemo(
    () =>
      createDraftValue(
        value,
        today,
        selectionMode,
        minDate,
        maxDate,
      ),
    [maxDate, minDate, selectionMode, today, value],
  );
  const yearCount = currentYearPosition === "only" ? 1 : 3;
  const [isOpen, setIsOpen] = useState(false);
  const [draft, setDraft] = useState<DatePickerValue>(initialDraft);
  const [viewYear, setViewYear] = useState(initialDraft.year);
  const [yearWindowStart, setYearWindowStart] = useState(() =>
    getInitialYearWindowStart(
      today.year,
      initialDraft.year,
      currentYearPosition,
    ),
  );
  const draftDay = draft.mode === "day" ? draft.day : undefined;
  const visibleYears = useMemo(
    () =>
      Array.from(
        { length: yearCount },
        (_, index) => yearWindowStart + index,
      ),
    [yearCount, yearWindowStart],
  );

  useEffect(() => {
    if (!isOpen) {
      setDraft((current) =>
        isSameDatePickerValue(current, initialDraft)
          ? current
          : initialDraft,
      );
      setViewYear(initialDraft.year);
      setYearWindowStart(
        getInitialYearWindowStart(
          today.year,
          initialDraft.year,
          currentYearPosition,
        ),
      );
    }
  }, [currentYearPosition, initialDraft, isOpen, today.year]);

  useEffect(() => {
    if (!isOpen) return;

    const handlePointerDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        setIsOpen(false);
        triggerRef.current?.focus();
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const focusTimer = window.setTimeout(() => {
      if (draft.mode === "day") {
        const selectedIndex = buildCalendarDays(
          draft.year,
          draft.month,
        ).findIndex(
          (day) =>
            day.isCurrentMonth &&
            day.year === draft.year &&
            day.month === draft.month &&
            day.day === draftDay,
        );
        dayRefs.current[selectedIndex]?.focus();
      } else {
        monthRefs.current[draft.month - 1]?.focus();
      }
    }, 0);

    return () => window.clearTimeout(focusTimer);
  }, [draftDay, draft.mode, draft.month, draft.year, isOpen]);

  const calendarDays = useMemo(
    () => buildCalendarDays(draft.year, draft.month),
    [draft.month, draft.year],
  );

  const commit = (nextValue: DatePickerValue) => {
    onChange(nextValue);
    setIsOpen(false);
    window.setTimeout(() => triggerRef.current?.focus(), 0);
  };

  const changeYear = (year: number) => {
    setViewYear(year);
    setDraft((current) => {
      const nextDate = clampCalendarDateToBounds(
        {
          year,
          month: current.month,
          day: current.mode === "day" ? current.day : 1,
        },
        minDate,
        maxDate,
      );

      return current.mode === "day"
        ? { mode: "day", ...nextDate }
        : {
            mode: "month",
            year: nextDate.year,
            month: nextDate.month,
          };
    });
  };

  const navigateYear = (offset: -1 | 1) => {
    const nextYear = viewYear + offset;
    changeYear(nextYear);

    if (nextYear < yearWindowStart) {
      setYearWindowStart(nextYear);
    } else if (nextYear >= yearWindowStart + yearCount) {
      setYearWindowStart(nextYear - yearCount + 1);
    }
  };

  const changeMode = (mode: DatePickerValue["mode"]) => {
    if (mode === draft.mode) return;

    if (mode === "month") {
      setDraft({ mode, year: draft.year, month: draft.month });
      return;
    }

    const preferredDay =
      draft.year === today.year && draft.month === today.month ? today.day : 1;
    const nextDate = clampCalendarDateToBounds(
      {
        year: draft.year,
        month: draft.month,
        day: preferredDay,
      },
      minDate,
      maxDate,
    );
    setDraft({ mode, ...nextDate });
  };

  const handleMonthClick = (month: number) => {
    if (draft.mode === "month") {
      commit({ mode: "month", year: viewYear, month });
      return;
    }

    setDraft((current) => {
      const nextDate = clampCalendarDateToBounds(
        {
          year: viewYear,
          month,
          day: current.mode === "day" ? current.day : 1,
        },
        minDate,
        maxDate,
      );

      return { mode: "day", ...nextDate };
    });
  };

  const focusGridItem = (
    refs: Array<HTMLButtonElement | null>,
    startIndex: number,
    offset: number,
  ) => {
    const candidate = refs[startIndex + offset];
    if (candidate && !candidate.disabled) candidate.focus();
  };

  const handleMonthKeyDown = (
    event: ReactKeyboardEvent<HTMLButtonElement>,
    index: number,
  ) => {
    const offsets: Record<string, number> = {
      ArrowLeft: -1,
      ArrowRight: 1,
      ArrowUp: -3,
      ArrowDown: 3,
    };
    const offset = offsets[event.key];
    if (offset !== undefined) {
      event.preventDefault();
      focusGridItem(monthRefs.current, index, offset);
    }
  };

  const handleDayKeyDown = (
    event: ReactKeyboardEvent<HTMLButtonElement>,
    index: number,
  ) => {
    const offsets: Record<string, number> = {
      ArrowLeft: -1,
      ArrowRight: 1,
      ArrowUp: -7,
      ArrowDown: 7,
      Home: -(index % 7),
      End: 6 - (index % 7),
    };
    const offset = offsets[event.key];
    if (offset !== undefined) {
      event.preventDefault();
      focusGridItem(dayRefs.current, index, offset);
    }
  };

  const selectedDate = toCalendarDate(draft);
  const isYearAvailable = (year: number) => {
    const allowsPast =
      currentYearPosition === "center" ||
      currentYearPosition === "right";
    const allowsFuture =
      currentYearPosition === "center" ||
      currentYearPosition === "left";

    return (
      (allowsPast || year >= today.year) &&
      (allowsFuture || year <= today.year) &&
      isYearWithinBounds(year, minDate, maxDate)
    );
  };
  const canGoPrevious = isYearAvailable(viewYear - 1);
  const canGoNext = isYearAvailable(viewYear + 1);
  const displayValue = value
    ? formatDatePickerValue(initialDraft, locale)
    : placeholder;

  return (
    <DatePickerRoot ref={rootRef} className={className}>
      <DatePickerTrigger
        ref={triggerRef}
        type="button"
        disabled={disabled}
        aria-label={`${ariaLabel}: ${displayValue}`}
        aria-describedby={ariaDescribedBy}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        aria-controls={isOpen ? panelId : undefined}
        onClick={() => setIsOpen((open) => !open)}
      >
        <span>{displayValue}</span>
        <TriggerChevron
          $open={isOpen}
          $appearance={appearance}
          aria-hidden="true"
        >
          <Icons
            name="iconArrow400"
            width={22}
            height={22}
            color="currentColor"
          />
        </TriggerChevron>
      </DatePickerTrigger>

      {isOpen && (
        <DatePickerPanel
          id={panelId}
          role="dialog"
          aria-modal="false"
          aria-label="Seleccionar período"
        >
          <YearNavigation>
            <YearButton
              type="button"
              aria-label="Año anterior"
              disabled={!canGoPrevious}
              onClick={() => navigateYear(-1)}
            >
              <YearArrowIcon $direction="left" aria-hidden="true" />
            </YearButton>
            <YearOptions $single={yearCount === 1} aria-live="polite">
              {visibleYears.map((year) => {
                const isSelected = year === viewYear;

                return (
                  <YearOption
                    key={year}
                    type="button"
                    aria-label={`Año ${year}`}
                    aria-pressed={isSelected}
                    $selected={isSelected}
                    disabled={!isYearAvailable(year)}
                    onClick={() => changeYear(year)}
                  >
                    {year}
                  </YearOption>
                );
              })}
            </YearOptions>
            <YearButton
              type="button"
              aria-label="Año siguiente"
              disabled={!canGoNext}
              onClick={() => navigateYear(1)}
            >
              <YearArrowIcon $direction="right" aria-hidden="true" />
            </YearButton>
          </YearNavigation>

          <MonthGrid role="grid" aria-label={`Meses de ${viewYear}`}>
            {MONTHS.map((month, index) => {
              const isSelected =
                draft.year === viewYear && draft.month === month;
              return (
                <MonthButton
                  key={month}
                  ref={(element) => {
                    monthRefs.current[index] = element;
                  }}
                  type="button"
                  role="gridcell"
                  aria-selected={isSelected}
                  $selected={isSelected}
                  disabled={
                    !isMonthWithinBounds(viewYear, month, minDate, maxDate)
                  }
                  onClick={() => handleMonthClick(month)}
                  onKeyDown={(event) => handleMonthKeyDown(event, index)}
                >
                  {formatMonthName(month, locale, "short")}
                </MonthButton>
              );
            })}
          </MonthGrid>

          {selectionMode === "both" && (
            <ModeSelector aria-label="Precisión del período">
              <ModeButton
                type="button"
                $selected={draft.mode === "month"}
                aria-pressed={draft.mode === "month"}
                onClick={() => changeMode("month")}
              >
                Mes completo
              </ModeButton>
              <ModeButton
                type="button"
                $selected={draft.mode === "day"}
                aria-pressed={draft.mode === "day"}
                onClick={() => changeMode("day")}
              >
                Por día puntual
              </ModeButton>
            </ModeSelector>
          )}

          {draft.mode === "day" && (
            <CalendarGrid role="grid" aria-label="Días del mes">
              {WEEKDAY_LABELS.map((weekday, index) => (
                <Weekday key={`${weekday}-${index}`} aria-hidden="true">
                  {weekday}
                </Weekday>
              ))}
              {calendarDays.map((day, index) => {
                const isSelected = isSameCalendarDate(day, selectedDate);
                const isToday = isSameCalendarDate(day, today);
                const disabledDay =
                  !day.isCurrentMonth ||
                  !isDateWithinBounds(day, minDate, maxDate);

                return (
                  <DayButton
                    key={`${day.year}-${day.month}-${day.day}`}
                    ref={(element) => {
                      dayRefs.current[index] = element;
                    }}
                    type="button"
                    role="gridcell"
                    aria-selected={isSelected}
                    aria-current={isToday ? "date" : undefined}
                    aria-label={`${day.day} de ${formatMonthName(day.month, locale, "long")} de ${day.year}`}
                    $selected={isSelected}
                    $outside={!day.isCurrentMonth}
                    disabled={disabledDay}
                    onKeyDown={(event) => handleDayKeyDown(event, index)}
                    onClick={() =>
                      commit({
                        mode: "day",
                        year: day.year,
                        month: day.month,
                        day: day.day,
                      })
                    }
                  >
                    {day.day}
                  </DayButton>
                );
              })}
            </CalendarGrid>
          )}
          <ScreenReaderText aria-live="polite">
            {draft.mode === "month"
              ? "Selección por mes completo"
              : "Selección por día puntual"}
          </ScreenReaderText>
        </DatePickerPanel>
      )}
    </DatePickerRoot>
  );
};
